import _get from "lodash/get";
import _isEmpty from "lodash/isEmpty";
import _join from "lodash/join";
import _map from "lodash/map";
import _partition from "lodash/partition";
import _replace from "lodash/replace";
import _size from "lodash/size";
import _split from "lodash/split";
import _startsWith from "lodash/startsWith";
import { AnyAction } from "redux";
import { ThunkAction } from "redux-thunk";
import { FORM_ID } from "../../../../../constants";
import { Discount, RootState } from "../../../../../models";
import {
    SubmitBusinessManualOrderRequestBody,
    SetBusinessDetailsRequestBody,
} from "../../../../../models/actionPayloads";
import { BusinessDetailsDetails, BusinessProduct, BusinessProductUser, OfferingB2B } from "../../../../../models/state";
import { get as Ajax_get, post as Ajax_post } from "../../../app/Ajax";
import { isBroadbandCode } from "../../../../../shared/subscriptionCodeUtils";
import * as generalActions from "../../../app/state/generalActions";
import { VALIDATION_FIELD } from "../../../rapidform/formValidation";
import { API_URL_SET_BUSINESS_DETAILS, API_URL_SUBMIT_MANUAL_ORDER } from "../../api-b2b";
import TEXT from "../../text-consts-b2b";
import * as AT from "../actionTypesB2B";
import * as BUSINESS_DETAILS_SELECTORS from "../selectors/businessDetailsSelectors";
import { getFormById } from "../selectors/formSelectors";

export const setShouldShowOrgOrNameInputField = (value: boolean) => ({
    type: AT.B2B_SET_SHOULD_SHOW_ORG_OR_NAME_INPUT,
    value,
});

export const setShouldShowInputFields = (value: boolean) => ({
    type: AT.B2B_SET_SHOULD_SHOW_INPUT_FIELDS,
    value,
});

export const setShouldShowPBFields = (value: boolean) => ({
    type: AT.B2B_SET_SHOULD_SHOW_PB_FIELDS,
    value,
});

export const checkOrgName = (orgName: string): ThunkAction<void, RootState, unknown, AnyAction> => {
    return function (dispatch) {
        if (_size(orgName) <= 1) {
            dispatch({
                type: AT.B2B_CHECK_ORGANIZATION_NAME,
                state: [],
            });
        } else
            Ajax_get<{ _embedded: { enheter: { navn: string; organisasjonsnummer: string }[] } }>(
                {
                    url: `https://data.brreg.no/enhetsregisteret/api/enheter?konkurs=false&navn=${encodeURIComponent(
                        orgName,
                    )}')`,
                },
                dispatch,
            )
                .then((brregResponse) => {
                    const s = _map(_get(brregResponse, "_embedded.enheter"), (d) => ({
                        name: d.navn,
                        orgNumber: d.organisasjonsnummer,
                    }));

                    dispatch({
                        type: AT.B2B_CHECK_ORGANIZATION_NAME,
                        state: s,
                    });
                })
                .catch((err) => {
                    dispatch({
                        type: AT.B2B_CHECK_ORGANIZATION_NAME,
                        state: [],
                    });
                    dispatch(
                        generalActions.setNegativeFlash(
                            err.displayable
                                ? err.error
                                : "Beklager, for øyeblikket fungerer ikke søk på organisasjons navn. Prøv med organisasjonsnummer",
                        ),
                    );
                });
    };
};

export const checkOrgNumber = (
    orgNumber: string,
    fieldSet?: string,
): ThunkAction<Promise<Partial<BusinessDetailsDetails>>, RootState, unknown, AnyAction> => {
    return function (dispatch) {
        const businessDetails: Partial<BusinessDetailsDetails> = {
            orgNumber,
        };

        const response = Ajax_get<{
            forretningsadresse?: { adresse: string; postnummer: string; poststed: string };
            navn: string;
        }>({ url: `https://data.brreg.no/enhetsregisteret/api/enheter/${orgNumber}` }, dispatch).then(
            (brregResponse) => {
                businessDetails.businessName = brregResponse.navn;
                businessDetails.accountName = brregResponse.navn;

                if (brregResponse.forretningsadresse) {
                    const [coAddresses, regularAddresses] = _partition(
                        brregResponse.forretningsadresse.adresse,
                        (address) => _startsWith(address.toLowerCase(), "c/o"),
                    );
                    const [pbAddresses, streetAddresses] = _partition(regularAddresses, (address) =>
                        _startsWith(address.toLowerCase(), "postboks"),
                    );
                    const parsedCoAddresses = _map(coAddresses, (address) => _replace(address, "c/o", "").trim());
                    const parsedPbAddresses = _map(pbAddresses, (address) => _replace(address, "Postboks", "").trim());
                    businessDetails.streetCO = _join(parsedCoAddresses, " ").trim();
                    businessDetails.street = _join(streetAddresses, " ").trim();
                    businessDetails.postcode = brregResponse.forretningsadresse.postnummer;
                    businessDetails.city = brregResponse.forretningsadresse.poststed;

                    if (!_isEmpty(parsedPbAddresses)) {
                        const pbAddress = parsedPbAddresses[0]; // Assume only 1 PB can exist
                        const [boxNumber, boxName] = _split(pbAddress, " ");
                        businessDetails.boxNumber = boxNumber;
                        businessDetails.boxName = boxName;
                    }
                }

                return { ...businessDetails, fieldSet };
            },
        );

        dispatch({
            type: AT.B2B_CHECK_ORGANIZATION_NUMBER,
            payload: response,
        });

        return response;
    };
};

export const setBusinessDetails = (): ThunkAction<void, RootState, unknown, AnyAction> => (dispatch, getState) => {
    const { values } = getFormById(getState(), FORM_ID.BUSINESS_DETAILS);
    const businessData = {
        organizationName: BUSINESS_DETAILS_SELECTORS.getOrganizationName(getState()),
        organizationNumber: BUSINESS_DETAILS_SELECTORS.getOrganizationNumber(getState()),
        postcode: values.hardwareOnly[VALIDATION_FIELD.POSTCODE],
        streetCO: values.hardwareOnly[VALIDATION_FIELD.STREET_CO],
        street: values.hardwareOnly[VALIDATION_FIELD.STREET],
        boxNumber: values.hardwareOnly[VALIDATION_FIELD.PB_NUMBER],
        boxName: values.hardwareOnly[VALIDATION_FIELD.PB_NAME],
        city: values.hardwareOnly[VALIDATION_FIELD.CITY],
        firstName: values.hardwareOnly[VALIDATION_FIELD.FIRST_NAME],
        lastName: values.hardwareOnly[VALIDATION_FIELD.LAST_NAME],
        email: _get(values, `hardwareOnly.${VALIDATION_FIELD.EMAIL}`, ""),
        phone: _get(values, `hardwareOnly.${VALIDATION_FIELD.PHONE}`, "").replace(/\s/g, ""),
    };

    const businessDetailsContainer: SetBusinessDetailsRequestBody = {
        businessData,
    };

    // Call setBusinessDetails API endpoint
    const response = Ajax_post(
        {
            url: API_URL_SET_BUSINESS_DETAILS,
            data: businessDetailsContainer,
        },
        dispatch,
    ).then(() => ({
        success: true,
    }));

    return dispatch({
        type: AT.B2B_SUBMIT_BUSINESS_DETAILS,
        payload: response,
        meta: {
            flashOnError: {
                defaultErrorMessage: TEXT.ERROR.ORDER_SUBMIT,
            },
        },
    });
};

export const submitManualOrder =
    ({
        ids,
        isNewNumbers,
        selectedSubscriptions,
        numberOfSubscriptions,
        product,
        wantInsurance,
        webdealsInCart,
    }: {
        ids: number[];
        isNewNumbers: boolean[];
        numberOfSubscriptions: number;
        product: Partial<BusinessProduct>;
        selectedSubscriptions: OfferingB2B[];
        wantInsurance: boolean;
        webdealsInCart: Discount[];
    }): ThunkAction<void, RootState, unknown, AnyAction> =>
    (dispatch, getState) => {
        const { values } = getFormById(getState(), FORM_ID.BUSINESS_DETAILS);
        const productUsers: BusinessProductUser[] = [];
        const isMbb = isBroadbandCode(selectedSubscriptions[0]?.offeringCode);

        for (let index = 0; index < numberOfSubscriptions; index++) {
            const productUser: BusinessProductUser = {
                productUserFirstName: _get(
                    values,
                    `productUser_${ids[index]}.${VALIDATION_FIELD.PRODUCT_USER_FIRST_NAME}`,
                    "",
                ),
                productUserLastName: _get(
                    values,
                    `productUser_${ids[index]}.${VALIDATION_FIELD.PRODUCT_USER_LAST_NAME}`,
                    "",
                ),
                productUserBirthDate: _get(
                    values,
                    `productUser_${ids[index]}.${VALIDATION_FIELD.PRODUCT_USER_BIRTHDATE}`,
                    "",
                ),
                productUserPhone: _get(values, `productUser_${ids[index]}.${VALIDATION_FIELD.PRODUCT_USER_PHONE}`, ""),
                offeringCode: selectedSubscriptions[index].offeringCode,
                offeringName: selectedSubscriptions[index].name,
                offeringPrice: selectedSubscriptions[index].monthlyPrice,
                isNewNumber: isNewNumbers[index],
                insurance: wantInsurance && !isMbb ? "Svitsj Plussforsikring (99,-/mnd)" : undefined,
            };

            // Append product to first subscription/productUser
            if (_get(product, "sku") && index === 0) {
                productUser.productSku = product.sku;
                productUser.productName = product.name;
                productUser.productMonthlyPrice = product.salesPriceWithoutVat;
            }
            if (isBroadbandCode(selectedSubscriptions[index].offeringCode)) {
                productUser.productUpfrontPrice = product.salesPriceWithoutVat;
            }

            productUsers.push(productUser);
        }

        const businessData = {
            organization: {
                name: BUSINESS_DETAILS_SELECTORS.getOrganizationName(getState()),
                number: BUSINESS_DETAILS_SELECTORS.getOrganizationNumber(getState()),
                postcode: values.manualOrder[VALIDATION_FIELD.POSTCODE],
                streetCO: values.manualOrder[VALIDATION_FIELD.STREET_CO],
                street: values.manualOrder[VALIDATION_FIELD.STREET],
                boxNumber: values.manualOrder[VALIDATION_FIELD.PB_NUMBER],
                boxName: values.manualOrder[VALIDATION_FIELD.PB_NAME],
                city: values.manualOrder[VALIDATION_FIELD.CITY],
            },
            contact: {
                firstName: values.manualOrder[VALIDATION_FIELD.FIRST_NAME],
                lastName: values.manualOrder[VALIDATION_FIELD.LAST_NAME],
                email: _get(values, `manualOrder.${VALIDATION_FIELD.CONTACT_EMAIL}`, ""),
                phone: _get(values, `manualOrder.${VALIDATION_FIELD.CONTACT_PHONE}`, "").replace(/\s/g, ""),
            },
            productUsers: productUsers,
            invoiceEmail: _get(values, `manualOrder.${VALIDATION_FIELD.EMAIL}`, ""),
            isMbb,
            webdealsInCart,
        };

        const businessDetailsContainer: SubmitBusinessManualOrderRequestBody = {
            businessData,
        };

        const response = Ajax_post<{ orderId: string }>(
            {
                url: API_URL_SUBMIT_MANUAL_ORDER,
                data: businessDetailsContainer,
            },
            dispatch,
        ).then(({ orderId }) => ({
            success: true,
            orderId,
        }));

        return dispatch({
            type: AT.B2B_SUBMIT_MANUAL_ORDER,
            payload: response,
            meta: {
                flashOnError: {
                    defaultErrorMessage: TEXT.ERROR.ORDER_SUBMIT,
                },
            },
        });
    };
